import * as React from 'react';
import * as ReactDOM from 'react-dom';
import Tooltip from './components/ButtonTooltip';
import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import FileType from '../data/FileType';
import Layer from '../data/Layer';
import AddFrameButton from './components/AddFrameButton';
import { FrameType } from '../data/FrameType';
import { generateClassName, selectLocalFiles } from '../utils';
import { trackEvent } from 'utils/Tracker';
import { AddTextFrameCommand } from 'state/commands/AddTextFrameCommand';
import { AddSolidColorCommand } from 'state/commands/AddSolidColorFrameCommand';
import { PlatformType } from 'data/PlatformType';
import { isFigmaPro } from 'state/AppState';

interface Props {
  layer?: Layer;
}

interface State {
  open: boolean;
}

interface MenuItemProps {
  type: FrameType;
  onClick: (event: React.MouseEvent<HTMLLIElement>) => void;
}

const MenuItem: React.FunctionComponent<MenuItemProps> = props => {
  return (
    <li onClick={props.onClick}>
      <i className={props.type} />
      {props.children}
    </li>
  );
};

class NewFrameTypeMenu extends React.Component<Props, State> {
  triggerBtn = React.createRef<HTMLButtonElement>();
  state: State = {
    open: false
  };
  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.open !== this.state.open) {
      if (this.state.open) {
        window.addEventListener('mousedown', this.didClickOnWindow);
        if (this.triggerBtn.current) {
          const origin = this.triggerBtn.current.getBoundingClientRect();
          const me = ReactDOM.findDOMNode(this) as HTMLElement;
          const menu = me.querySelector('.frame-types-menu') as HTMLElement;
          if (menu) {
            const menuHeight = menu.getBoundingClientRect().height;
            menu.style.left = origin.right + 2 + 'px';
            menu.style.top =
              Math.min(
                origin.top,
                (window.innerHeight as number) - menuHeight
              ) + 'px';
          }
        }
      } else {
        window.removeEventListener('mousedown', this.didClickOnWindow);
      }
    }
  }
  componentWillUnmount() {
    window.removeEventListener('mousedown', this.didClickOnWindow);
  }
  didClickOnWindow = (e: MouseEvent) => {
    const me = ReactDOM.findDOMNode(this) as Element;
    const menu = me.querySelector('.frame-types-menu');
    if (!menu || !menu.contains(e.target as HTMLElement)) {
      this.setState({ open: false });
    }
  };

  showMenu = () => {
    this.setState({ open: true });
  };

  didSelectOption = async (type: FrameType) => {
    this.setState({ open: false });
    const { app } = getStoreState();
    const { platform } = app;
    const isLimited = platform === PlatformType.Figma && !isFigmaPro(app);
    const { layer } = this.props;
    switch (type) {
      case FrameType.Unknown:
        const selectType = this.hasLayer ? FileType.Image : FileType.Graphic;
        const files = await selectLocalFiles(selectType, !this.hasLayer);
        if (files.length) {
          storeDispatch(Actions.addLocalGraphics(files, layer));
        }
        break;
      case FrameType.Text:
        storeDispatch(new AddTextFrameCommand(layer?.id));
        break;
      case FrameType.Captions:
        if (isLimited) {
          storeDispatch(Actions.showBuyFigma('captions'));
          return;
        }
        storeDispatch(Actions.addCaptions(layer));
        break;
      case FrameType.Shape:
        storeDispatch(Actions.showShapeSelector(layer));
        break;
      case FrameType.SolidColor:
        storeDispatch(new AddSolidColorCommand(layer?.id));
        break;
      case FrameType.Gradient:
        storeDispatch(Actions.showGradientSelector(layer));
        break;
      case FrameType.Pattern:
        storeDispatch(Actions.showPatternSelector(layer));
        break;
      case FrameType.PhotoLibrary:
        storeDispatch(Actions.showUnsplashSelector(layer));
        break;
      case FrameType.SoundWave:
        if (isLimited) {
          storeDispatch(Actions.showBuyFigma('sound wave'));
          return;
        }
        storeDispatch(Actions.showSoundWaveSelector(layer));
        break;
      case FrameType.Sticker:
        storeDispatch(Actions.showStickerSelector(layer));
        break;
      default:
        break;
    }
    trackEvent('Add frame', { type });
  };

  get hasLayer(): boolean {
    return this.props.layer !== undefined;
  }

  render() {
    const isFigma = getStoreState().app.platform === PlatformType.Figma;
    const data = isFigma
      ? [
          {
            type: FrameType.Captions,
            label: 'Captions'
          },
          {
            type: FrameType.Sticker,
            label: 'Sticker'
          },
          {
            type: FrameType.SoundWave,
            label: 'Sound wave'
          }
        ]
      : [
          {
            type: FrameType.Unknown,
            label: `Your image${!this.hasLayer ? '/video' : ''}`
          },
          {
            type: FrameType.Text,
            label: 'Text'
          },
          {
            type: FrameType.Captions,
            label: 'Captions'
          },
          {
            type: FrameType.Sticker,
            label: 'Sticker'
          },
          {
            type: FrameType.PhotoLibrary,
            label: 'Professional photo'
          },
          {
            type: FrameType.SoundWave,
            label: 'Sound wave'
          },
          {
            type: FrameType.Shape,
            label: 'Shape'
          },
          {
            type: FrameType.SolidColor,
            label: 'Solid color'
          },
          {
            type: FrameType.Gradient,
            label: 'Gradient'
          },
          {
            type: FrameType.Pattern,
            label: 'Cool background'
          }
        ];
    return (
      <span
        className={generateClassName('new-frame-menu', {
          hasLayer: this.hasLayer,
          open: this.state.open
        })}
      >
        {this.props.layer ? (
          <Tooltip text="Add frame" hide={this.state.open}>
            <AddFrameButton
              innerRef={this.triggerBtn}
              onClick={this.showMenu}
            />
          </Tooltip>
        ) : (
          <Tooltip placement="topLeft" text="Add layer" hide={this.state.open}>
            <button
              ref={this.triggerBtn}
              className="icon plus"
              onClick={this.showMenu}
            />
          </Tooltip>
        )}
        {this.state.open && (
          <div className="frame-types-menu">
            {data.map(item => (
              <MenuItem
                type={item.type}
                key={item.type}
                onClick={e => this.didSelectOption(item.type)}
              >
                {item.label}
              </MenuItem>
            ))}
          </div>
        )}
      </span>
    );
  }
}

export { NewFrameTypeMenu };



// WEBPACK FOOTER //
// ./src/controller/NewFrameTypeMenu.tsx